import * as z from "zod";

/**
 * How a value reads: `currency` is USD dollars whatever the store sells in
 * (see `native` for the recorded currency), `percent` is a fraction (0.02 =
 * 2%), and `count` is a plain number.
 */
export const homeMetricFormats = ["currency", "percent", "count"] as const;

/**
 * An amount in the currency Redo recorded it in, which for most merchants is
 * the store's own. Carried beside the USD figure so a page can show the real
 * amount rather than one converted at today's rate.
 */
const NativeAmountSchema_ = z.object({
  amount: z.number(),
  currency: z.string(),
});

const HomeMetricSchema_ = z.object({
  /**
   * Identifies the metric within its product, e.g. "returnRate" under Returns.
   * Scoped rather than global, so a name only has to be unique where it means
   * something. Stable: it survives a change to the formula behind it.
   */
  type: z.string(),
  /** English fallback; the client translates by `type`. */
  label: z.string(),
  format: z.enum(homeMetricFormats),
  value: z.number(),
  /**
   * The count the source reports behind the figure, usually events (e.g.
   * returns), so a merchant can check the amount against something they can
   * count. Null when the source reports none.
   */
  count: z.number().nullable(),
  /**
   * Null for figures that aren't money, and when the source doesn't record one
   * currency and amount on every row.
   */
  native: NativeAmountSchema_.nullable(),
});

export const HomeMetricSchema: z.ZodType<HomeMetricSchema> = HomeMetricSchema_;
export interface HomeMetricSchema extends z.infer<typeof HomeMetricSchema_> {}

const HomeProductMetricsSchema_ = z.object({
  /**
   * The Redo product that earns these figures, as the warehouse labels it —
   * not an enum we maintain, so a product we never enumerated still appears.
   */
  product: z.string(),
  /**
   * The cloud the product belongs to, for surfaces that group that way. Empty
   * when no source names one.
   */
  productCloud: z.string(),
  /** Never empty: a product with nothing to show is left out entirely. */
  metrics: z.array(HomeMetricSchema),
});

export const HomeProductMetricsSchema: z.ZodType<HomeProductMetricsSchema> =
  HomeProductMetricsSchema_;
export interface HomeProductMetricsSchema extends z.infer<
  typeof HomeProductMetricsSchema_
> {}

export const getHomeMetrics = {
  name: "getHomeMetrics" as const,
  input: z.null(),
  output: z.object({
    /**
     * The last day the figures cover, as YYYY-MM-DD. They span the 30 days
     * ending on it, so a client can format the range for its locale.
     */
    periodKey: z.string(),
    /** English fallback for the range, e.g. "Sep 13 – Oct 12, 2026". */
    periodLabel: z.string(),
    /**
     * Grouped by product, so a figure is reached by product and then type
     * rather than by scanning one flat pool. Only products this merchant
     * actually has data for, in the order the registry declares them.
     */
    products: z.array(HomeProductMetricsSchema),
  }),
};
